/**
 * Authentication service for Pixiv Downloader
 * Uses backend API with JWT tokens
 */

// Use Cloudflare Pages Functions for auth, external backend for downloads
const AUTH_BASE_URL = window.location.origin; // Same domain as frontend
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

class AuthService {
  constructor() {
    this.token = localStorage.getItem('auth_token');
    this.user = JSON.parse(localStorage.getItem('user') || 'null');
  }

  // Get auth headers
  getAuthHeaders() {
    return this.token ? {
      'Authorization': `Bearer ${this.token}`,
      'Content-Type': 'application/json'
    } : {
      'Content-Type': 'application/json'
    };
  }

  // Sign up new user
  async signUp(email, password, username) {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, username })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Registration failed');
      }
      
      // Store token and user
      this.token = data.access_token;
      this.user = data.user;
      localStorage.setItem('auth_token', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Sign in user
  async signIn(email, password) {
    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      // Store token and user
      this.token = data.access_token;
      this.user = data.user;
      localStorage.setItem('auth_token', this.token);
      localStorage.setItem('user', JSON.stringify(this.user));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Sign out user
  async signOut() {
    this.token = null;
    this.user = null;
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  }

  // Get current user
  async getCurrentUser() {
    if (!this.token) {
      return { user: null, profile: null };
    }

    try {
      const response = await fetch(`${AUTH_BASE_URL}/api/auth/me`, {
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        // Token might be expired
        await this.signOut();
        return { user: null, profile: null };
      }

      const user = await response.json();
      this.user = user;
      localStorage.setItem('user', JSON.stringify(user));

      return { user, profile: user };
    } catch (error) {
      console.error('Failed to get current user:', error);
      await this.signOut();
      return { user: null, profile: null };
    }
  }

  // Update user profile
  async updateProfile(updates) {
    try {
      const response = await fetch(`${API_BASE_URL}/users/settings`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ settings: updates })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Update failed');
      }

      this.user = data;
      localStorage.setItem('user', JSON.stringify(data));

      return { data, error: null };
    } catch (error) {
      return { data: null, error };
    }
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!(this.token && this.user);
  }

  // Get current user (synchronous)
  getUser() {
    return this.user;
  }

  // Listen to auth changes (for compatibility with existing code)
  onAuthStateChange(callback) {
    // Simple implementation - call callback immediately with current state
    callback({ data: { user: this.user } });
    
    // Return unsubscribe function
    return () => {};
  }
}

// Create singleton instance
const authService = new AuthService();

// Export auth helpers for compatibility
export const authHelpers = {
  signUp: (email, password, username) => authService.signUp(email, password, username),
  signIn: (email, password) => authService.signIn(email, password),
  signOut: () => authService.signOut(),
  getCurrentUser: () => authService.getCurrentUser(),
  updateProfile: (updates) => authService.updateProfile(updates),
  onAuthStateChange: (callback) => authService.onAuthStateChange(callback)
};

// Export service instance
export default authService;

// Export helper function
export const isAuthConfigured = () => true; // Always true for backend auth